// Decrypt in the browser (docs/07 "Conversation keys", docs/09 "Web UI"): unwrap the
// segment CEK wrapped to this device from `segment.open`, decrypt events under it, and
// seal the device's own answers and instructions. The KDF, AEAD and envelope are the
// crate's; WebCrypto contributes only the ECDH with the non-extractable key, the random
// nonce and the ECDSA signature.
import { bytesEqual, concat, randomBytes } from "../proto/bytes";
import { eventFromCbor, eventToCbor, headerToCbor } from "../proto/event-cbor";
import type { EncryptedEvent, EventType, IdBytes, InboundMessage, SegmentOpen } from "../proto/frames";
import type { Proto } from "../proto/index";
import { newIdBytes } from "../proto/ulid";
import { AUTH_ALG, type DeviceKeys, ecdh, signDomain } from "./keys";

/** A segment's content key, held in memory only. */
export type Cek = Uint8Array;

/**
 * The CEK of `open` for this device, or `undefined` when the segment was not wrapped to
 * it (the browser was linked after the segment opened and nothing re-wrapped it yet).
 */
export async function unwrapCek(proto: Proto, keys: DeviceKeys, deviceId: IdBytes, open: SegmentOpen): Promise<Cek | undefined> {
  const wrapped = open.keys.find((k) => bytesEqual(k.device_id, deviceId));
  if (!wrapped) return undefined;
  const dh = await ecdh(keys.encryption.privateKey, proto.wrappedCekEnc(wrapped.wrapped_cek));
  return proto.unwrapCekWithSharedSecret(
    dh,
    keys.encryptionPublicKey,
    wrapped.wrapped_cek,
    open.conversation_id,
    open.segment_id,
    deviceId,
  );
}

/** Plaintext of one event; throws when the CEK is wrong or the event was tampered with. */
export function decryptEvent(proto: Proto, cek: Cek, event: EncryptedEvent): Uint8Array {
  return proto.decryptEvent(cek, eventToCbor(proto, event));
}

export interface InboundInput {
  conversationId: IdBytes;
  segmentId: IdBytes;
  /** The device's own counter within the segment. */
  seq: number;
  eventType: Extract<EventType, "answer" | "instruction">;
  plaintext: Uint8Array;
}

/**
 * Encrypt and sign an inbound message (docs/07 "Inbound"): random nonce, `direction =
 * inbound`, signature over `message_id ‖ device_id ‖ AAD ‖ nonce ‖ tag ‖ ciphertext`.
 */
export async function sealInbound(
  proto: Proto,
  keys: DeviceKeys,
  deviceId: IdBytes,
  cek: Cek,
  input: InboundInput,
  messageId: IdBytes = newIdBytes(),
  nonce: Uint8Array = randomBytes(12),
): Promise<InboundMessage> {
  const header = headerToCbor(proto, {
    conversation_id: input.conversationId,
    segment_id: input.segmentId,
    seq: input.seq,
    event_type: input.eventType,
    direction: "inbound",
  });
  const event = eventFromCbor(proto, proto.encryptInboundEvent(cek, nonce, header, input.plaintext));
  const signing = inboundSigningBytes(proto, messageId, deviceId, event);
  const signature = await signDomain(keys.auth.privateKey, "ocs/v0/inbound", signing);
  return { message_id: messageId, device_id: deviceId, event, signature: { alg: AUTH_ALG, bytes: signature } };
}

/** `ocs_proto::inbound::InboundMessage::signing_bytes`. */
export function inboundSigningBytes(proto: Proto, messageId: IdBytes, deviceId: IdBytes, event: EncryptedEvent): Uint8Array {
  const aad = proto.eventAad(headerToCbor(proto, event.header));
  return concat(messageId, deviceId, aad, event.nonce, event.tag, event.ciphertext);
}
